/**
 * Simple in-memory cache for recruiting data
 * Stores latest fetched data to eliminate latency
 */
export class DataCache {
    constructor() {
        this.userData = null;
        this.organization = null;
        this.pendingInvitations = [];
        this.jobs = [];
        this.candidates = [];
        this.settings = null;
        this.dataSummary = null;
        this.chatMessages = new Map();
        /** Per loaded chat: the cursor of its earlier messages not loaded yet; null once its start is loaded. */
        this.chatEarlierCursors = new Map();
        this.scheduledMessages = new Map();
        this.lastUpdateTimestamp = 0;
    }
    /**
     * Update cached data from polling response
     */
    updateData(data) {
        let hasChanges = false;
        if (data.userData) {
            if (this.hasDataChanged(this.userData, data.userData)) {
                this.userData = data.userData;
                hasChanges = true;
            }
        }
        if (data.organization) {
            if (this.hasDataChanged(this.organization, data.organization)) {
                this.organization = data.organization;
                hasChanges = true;
            }
        }
        if (data.pendingInvitations) {
            this.pendingInvitations = data.pendingInvitations;
            hasChanges = true;
        }
        if (data.jobs) {
            if (this.hasDataChanged(this.jobs, data.jobs)) {
                this.jobs = data.jobs;
                hasChanges = true;
            }
        }
        if (data.candidates) {
            if (this.hasDataChanged(this.candidates, data.candidates)) {
                this.candidates = data.candidates;
                hasChanges = true;
            }
        }
        if (data.settings) {
            if (this.hasDataChanged(this.settings, data.settings)) {
                this.settings = data.settings;
                hasChanges = true;
            }
        }
        if (data.dataSummary) {
            if (this.hasDataChanged(this.dataSummary, data.dataSummary)) {
                this.dataSummary = data.dataSummary;
                hasChanges = true;
            }
        }
        if (data.scheduledMessages) {
            this.scheduledMessages.clear();
            for (const m of data.scheduledMessages) {
                this.scheduledMessages.set(m.id, m);
            }
            hasChanges = true;
        }
        this.lastUpdateTimestamp = data.timestamp;
        return hasChanges;
    }
    /**
     * Get cached user data
     */
    getUserData() {
        return this.userData;
    }
    /**
     * Get cached organization
     */
    getOrganization() {
        return this.organization;
    }
    getPendingInvitations() {
        return this.pendingInvitations;
    }
    /** Replace the pending-invitations list (mutation delta or notification). */
    setPendingInvitations(invitations) {
        this.pendingInvitations = invitations;
    }
    /**
     * Update cached user data
     */
    updateUserData(userData) {
        this.userData = userData;
    }
    /**
     * Get cached jobs — returns direct reference (readonly). Do not mutate.
     */
    getJobs() {
        return this.jobs;
    }
    /**
     * Get cached candidates — returns direct reference (readonly). Do not mutate.
     */
    getCandidates() {
        return this.candidates;
    }
    /**
     * Get cached unified settings
     */
    getSettings() {
        return this.settings;
    }
    /**
     * Get cached data summary
     */
    getDataSummary() {
        return this.dataSummary;
    }
    /**
     * Set cached data summary
     */
    setDataSummary(dataSummary) {
        this.dataSummary = dataSummary;
    }
    /**
     * Get last update timestamp
     */
    getLastUpdateTimestamp() {
        return this.lastUpdateTimestamp;
    }
    /**
     * Get cached chat messages for a specific chat
     */
    getChatMessages(chatId) {
        return this.chatMessages.get(chatId) || [];
    }
    /**
     * Set cached chat messages for a specific chat
     */
    setChatMessages(chatId, messages) {
        this.chatMessages.set(chatId, messages);
    }
    /** Whether a chat's newest page was loaded from the server. */
    /** Conversations whose newest page was loaded. */
    loadedChatIds() {
        return [...this.chatEarlierCursors.keys()];
    }
    isChatLoaded(chatId) {
        return this.chatEarlierCursors.has(chatId);
    }
    getChatEarlierCursor(chatId) {
        return this.chatEarlierCursors.get(chatId) ?? null;
    }
    setChatEarlierCursor(chatId, cursor) {
        this.chatEarlierCursors.set(chatId, cursor);
    }
    // ─── Scheduled (anti-ghosting) messages ──────────────────────────────────
    //
    // Recruiter table reads `JobApplication.followUpSummary` (denormalized,
    // P1-3); the per-candidate timeline filters this map. Notifications
    // (`SCHEDULED_MESSAGE(S)_UPDATED`) patch via `upsertScheduledMessage`.
    /** Filter scheduled messages for a (candidate, job) pair, ordered by `scheduledFor` asc. */
    getScheduledMessagesFor(candidateId, jobId) {
        const out = [];
        for (const m of this.scheduledMessages.values()) {
            if (m.candidateId !== candidateId)
                continue;
            if (jobId && m.jobId !== jobId)
                continue;
            out.push(m);
        }
        out.sort((a, b) => a.scheduledForUtc - b.scheduledForUtc);
        return out;
    }
    /** Patch a single scheduled message into the cache (notification or mutation delta). */
    upsertScheduledMessage(message) {
        this.scheduledMessages.set(message.id, message);
    }
    /** Bulk patch — used by `SCHEDULED_MESSAGES_UPDATED` notifications. */
    upsertScheduledMessages(messages) {
        for (const m of messages)
            this.scheduledMessages.set(m.id, m);
    }
    /** Drop a message from the cache (admin hard-delete via
     *  `SCHEDULED_MESSAGE_DELETED`). */
    removeScheduledMessage(id) {
        this.scheduledMessages.delete(id);
    }
    /** Admin global view: every scheduled message in the cache, sorted by
     *  `scheduledFor` ascending. Recruiters only ever see their own org;
     *  admins see all orgs because login pre-populated them. */
    getAllScheduledMessages() {
        const out = Array.from(this.scheduledMessages.values());
        out.sort((a, b) => a.scheduledForUtc - b.scheduledForUtc);
        return out;
    }
    /**
     * Clear all cached data
     */
    clear() {
        this.userData = null;
        this.organization = null;
        this.pendingInvitations = [];
        this.jobs = [];
        this.candidates = [];
        this.settings = null;
        this.dataSummary = null;
        this.chatMessages.clear();
        this.chatEarlierCursors.clear();
        this.scheduledMessages.clear();
        this.lastUpdateTimestamp = 0;
    }
    /**
     * Check if job exists in cache
     */
    hasJob(jobId) {
        return this.jobs.some(job => job.id === jobId);
    }
    /**
     * Get specific job from cache
     */
    getJob(jobId) {
        return this.jobs.find(job => job.id === jobId) || null;
    }
    /**
     * Check if candidate exists in cache
     */
    hasCandidate(candidateId) {
        return this.candidates.some(candidate => candidate.id === candidateId);
    }
    /**
     * Get specific candidate from cache
     */
    getCandidate(candidateId) {
        return this.candidates.find(candidate => candidate.id === candidateId);
    }
    /**
     * Add or update a single job in cache
     */
    updateJob(job) {
        const index = this.jobs.findIndex(j => j.id === job.id);
        if (index >= 0) {
            this.jobs[index] = job;
        }
        else {
            this.jobs.push(job);
        }
    }
    /**
     * Remove job from cache
     */
    removeJob(jobId) {
        this.jobs = this.jobs.filter(job => job.id !== jobId);
    }
    /**
     * Add or update a single candidate in cache
     */
    updateCandidate(candidate) {
        const index = this.candidates.findIndex(c => c.id === candidate.id);
        if (index >= 0) {
            this.candidates[index] = candidate;
        }
        else {
            this.candidates.push(candidate);
        }
    }
    /**
     * Remove candidate from cache
     */
    removeCandidate(candidateId) {
        this.candidates = this.candidates.filter(candidate => candidate.id !== candidateId);
    }
    /**
     * Simple data change detection using JSON comparison
     * For production, consider using a more sophisticated diff algorithm
     */
    hasDataChanged(oldData, newData) {
        return JSON.stringify(oldData) !== JSON.stringify(newData);
    }
}
